import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { AnalyticsConfigurationModal } from "./AnalyticsConfigurationModal";
import { AnalyticsSetupWorkflow } from "./AnalyticsSetupWorkflow";
import { DataSourceConnector } from "./DataSourceConnector";
import { BarChart3, Database, Settings, Workflow } from "lucide-react";

export const AnalyticsTestPage: React.FC = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  return (
    <div className="p-8 min-h-screen bg-background">
      <div className="max-w-6xl mx-auto space-y-6">
        <div className="text-center space-y-4">
          <div className="flex items-center justify-center gap-2">
            <BarChart3 className="w-8 h-8 text-primary" />
            <h1 className="text-3xl font-bold">Analytics System Test Suite</h1>
          </div>
          <p className="text-lg text-muted-foreground">
            Comprehensive testing interface for TerraMatrix Analytics Sprint 1 components and workflows.
          </p>
        </div>

        <Tabs defaultValue="workflow" className="w-full">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="workflow" className="flex items-center gap-2">
              <Workflow className="h-4 w-4" />
              Complete Workflow
            </TabsTrigger>
            <TabsTrigger value="datasource" className="flex items-center gap-2">
              <Database className="h-4 w-4" />
              Data Sources
            </TabsTrigger>
            <TabsTrigger value="config" className="flex items-center gap-2">
              <Settings className="h-4 w-4" />
              Configuration
            </TabsTrigger>
            <TabsTrigger value="docs" className="flex items-center gap-2">
              <BarChart3 className="h-4 w-4" />
              Documentation
            </TabsTrigger>
          </TabsList>

          <TabsContent value="workflow">
            <Card>
              <CardHeader>
                <CardTitle>End-to-End Analytics Setup Workflow</CardTitle>
                <p className="text-sm text-muted-foreground">
                  Test the complete analytics setup process from data source connection through cube schema generation.
                </p>
              </CardHeader>
              <CardContent>
                <AnalyticsSetupWorkflow
                  industryContext="agriculture"
                  autoAdvance={false}
                  onComplete={(result) => {
                    console.log('Workflow completed:', result);
                  }}
                />
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="datasource">
            <Card>
              <CardHeader>
                <CardTitle>Data Source Connection Testing</CardTitle>
                <p className="text-sm text-muted-foreground">
                  Test individual data source connection, validation, and management features.
                </p>
              </CardHeader>
              <CardContent>
                <DataSourceConnector
                  onConnectionSuccess={(source) => {
                    console.log('Data source connected:', source);
                  }}
                  onConnectionError={(error) => {
                    console.error('Connection failed:', error);
                  }}
                />
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="config">
            <div className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle>Analytics Configuration Modal</CardTitle>
                  <p className="text-sm text-muted-foreground">
                    Test the analytics configuration interface and settings management.
                  </p>
                </CardHeader>
                <CardContent>
                  <div className="flex justify-center">
                    <Button
                      onClick={() => setIsModalOpen(true)}
                      size="lg"
                      className="flex items-center gap-2"
                    >
                      <Settings className="w-5 h-5" />
                      Open Analytics Configuration
                    </Button>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Configuration Features to Test</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-3">
                      <h3 className="font-medium">Core Functionality</h3>
                      <ul className="space-y-2 text-sm">
                        <li className="flex items-center gap-2">
                          <div className="w-2 h-2 bg-primary rounded-full"></div>
                          Modal opens and closes properly
                        </li>
                        <li className="flex items-center gap-2">
                          <div className="w-2 h-2 bg-primary rounded-full"></div>
                          All tabs are accessible and functional
                        </li>
                        <li className="flex items-center gap-2">
                          <div className="w-2 h-2 bg-primary rounded-full"></div>
                          Form validation for input ranges
                        </li>
                        <li className="flex items-center gap-2">
                          <div className="w-2 h-2 bg-primary rounded-full"></div>
                          Unsaved changes warning
                        </li>
                      </ul>
                    </div>
                    <div className="space-y-3">
                      <h3 className="font-medium">User Experience</h3>
                      <ul className="space-y-2 text-sm">
                        <li className="flex items-center gap-2">
                          <div className="w-2 h-2 bg-primary rounded-full"></div>
                          Save button state management
                        </li>
                        <li className="flex items-center gap-2">
                          <div className="w-2 h-2 bg-primary rounded-full"></div>
                          Loading states work correctly
                        </li>
                        <li className="flex items-center gap-2">
                          <div className="w-2 h-2 bg-primary rounded-full"></div>
                          Keyboard navigation support
                        </li>
                        <li className="flex items-center gap-2">
                          <div className="w-2 h-2 bg-primary rounded-full"></div>
                          Accessibility compliance
                        </li>
                      </ul>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="docs">
            <Card>
              <CardHeader>
                <CardTitle>Sprint 1 Implementation Summary</CardTitle>
                <p className="text-sm text-muted-foreground">
                  Overview of completed features and components from Analytics Sprint 1.
                </p>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  <div className="space-y-3">
                    <h3 className="font-medium flex items-center gap-2">
                      <Database className="h-4 w-4" />
                      Data Source Components
                    </h3>
                    <ul className="space-y-1 text-sm text-muted-foreground">
                      <li>• DataSourceConnector</li>
                      <li>• ConnectionForm</li>
                      <li>• ConnectionTester</li>
                      <li>• Multi-source support</li>
                      <li>• Real-time validation</li>
                    </ul>
                  </div>

                  <div className="space-y-3">
                    <h3 className="font-medium flex items-center gap-2">
                      <BarChart3 className="h-4 w-4" />
                      Schema Discovery
                    </h3>
                    <ul className="space-y-1 text-sm text-muted-foreground">
                      <li>• AI-powered discovery</li>
                      <li>• SchemaTree visualization</li>
                      <li>• FieldEditor interface</li>
                      <li>• Business name suggestions</li>
                      <li>• Industry context enhancement</li>
                    </ul>
                  </div>

                  <div className="space-y-3">
                    <h3 className="font-medium flex items-center gap-2">
                      <Settings className="h-4 w-4" />
                      Cube.js Integration
                    </h3>
                    <ul className="space-y-1 text-sm text-muted-foreground">
                      <li>• CubeSchemaBuilder</li>
                      <li>• Automatic schema generation</li>
                      <li>• Pre-aggregation optimization</li>
                      <li>• Performance tuning</li>
                      <li>• SQL preview</li>
                    </ul>
                  </div>

                  <div className="space-y-3">
                    <h3 className="font-medium flex items-center gap-2">
                      <Workflow className="h-4 w-4" />
                      WebSocket Integration
                    </h3>
                    <ul className="space-y-1 text-sm text-muted-foreground">
                      <li>• Real-time progress updates</li>
                      <li>• Schema discovery events</li>
                      <li>• Analytics metrics streaming</li>
                      <li>• Error handling</li>
                      <li>• Collaborative editing support</li>
                    </ul>
                  </div>

                  <div className="space-y-3">
                    <h3 className="font-medium flex items-center gap-2">
                      <Database className="h-4 w-4" />
                      Service Integration
                    </h3>
                    <ul className="space-y-1 text-sm text-muted-foreground">
                      <li>• ServiceRegistry integration</li>
                      <li>• DataSourceService</li>
                      <li>• Analytics operations</li>
                      <li>• Caching strategies</li>
                      <li>• Error recovery</li>
                    </ul>
                  </div>

                  <div className="space-y-3">
                    <h3 className="font-medium flex items-center gap-2">
                      <BarChart3 className="h-4 w-4" />
                      Complete Workflow
                    </h3>
                    <ul className="space-y-1 text-sm text-muted-foreground">
                      <li>• End-to-end setup process</li>
                      <li>• Progressive step advancement</li>
                      <li>• Error recovery and retry</li>
                      <li>• Agricultural context support</li>
                      <li>• Production-ready integration</li>
                    </ul>
                  </div>
                </div>

                <div className="mt-6 p-4 bg-muted rounded-lg">
                  <h3 className="font-medium mb-2">Sprint 1 Goals Achieved ✅</h3>
                  <p className="text-sm text-muted-foreground">
                    All sprint objectives have been successfully implemented including data source discovery interfaces,
                    AI-powered schema generation, real-time WebSocket integration, and comprehensive testing capabilities.
                    The system is now ready for integration with the TerraMatrix platform and ready for Sprint 2 development.
                  </p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        <AnalyticsConfigurationModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          projectId="test-project"
        />
      </div>
    </div>
  );
};